import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AvatarVariant } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink, Pencil } from '@factorialco/f0-react/icons/app'
import copy from 'copy-to-clipboard'
import {
  AtsDuplicateJobPostingDocument,
  AtsJobPostingHeaderCurrentHiringRoleFragment,
  AtsJobPostingHeaderFragment,
  AtsJobPostingHeaderPoliciesFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl as buildAvatarUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { statusColor, statusLabel } from 'modules/ats/components/JobStatusTag'
import { ArchiveJobPostingModal } from 'modules/ats/components/job_postings/ArchiveJobPostingModal'
import { CancelJobPostingModal } from 'modules/ats/components/job_postings/CancelJobPostingModal'
import { DeleteJobPostingModal } from 'modules/ats/components/job_postings/DeleteJobPostingModal/DeleteJobPostingModal'
import { PublishJobPostingModal } from 'modules/ats/components/job_postings/PublishJobPostingModal'
import { PublishedJobPostingModal } from 'modules/ats/components/job_postings/PublishJobPostingModal/PublishedJobPostingModal'
import { useJobPostingPreview } from 'modules/ats/lib/utils/useJobPostingPreview'
import { CompanyPageNotConfiguredModal } from 'modules/company_page/components/CompanyPageNotConfiguredModal'
import { useCompanyPageSettings } from 'modules/company_page/hooks/useCompanyPageSettings'

interface Props {
  jobData: AtsJobPostingHeaderFragment & {
    currentHiringRole: AtsJobPostingHeaderCurrentHiringRoleFragment | null
  }
  isCompanyPageInstalled: boolean
  policies: AtsJobPostingHeaderPoliciesFragment
  isRequisitionsEnabled: boolean
}

export const JobPostingHeader: React.FC<Props> = ({
  jobData,
  isCompanyPageInstalled,
  policies,
  isRequisitionsEnabled,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { buildUrl, publicUrl } = useCompanyPageSettings()
  const { company, access } = useCurrentSession()
  const location = useLocation()

  const {
    id,
    title,
    status,
    location: jobLocation,
    team,
    hiringManagers,
    currentHiringRole,
    requisition,
    numberOfVacancies,
    startDate,
  } = jobData

  const [openedModal, setOpenedModal] = useState<
    'archive' | 'cancel' | 'company-page-warning' | 'publish' | 'published' | 'delete' | undefined
  >(location.state?.showPublishedModal ? 'published' : undefined)

  const isCompanyPageConfigured = !!company.slug && !!company.countryFactorialDomain
  const companyPageReady = isCompanyPageInstalled && isCompanyPageConfigured
  const canCreateJobPostings = policies.createJobPostings.can && !isRequisitionsEnabled
  const canManageJobPostings = policies.manageJobPostings.can

  const isReviewerRole = currentHiringRole?.hiringRole === 'reviewer' && !canManageJobPostings
  const isEditorRole = currentHiringRole?.hiringRole === 'editor' && !canManageJobPostings

  const isJobPostingOpen = status === 'published' || status === 'unlisted'

  const showDuplicateJobButton =
    canManageJobPostings || (!isRequisitionsEnabled && canCreateJobPostings)

  const getPreviewUrl = useJobPostingPreview({ id: id, status: status })

  const careersUrl = publicUrl || ''
  const jobUrl = buildUrl(`/job_posting/${id}`)

  const copyUrl = async () => {
    if (copy(jobUrl)) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.job_details.url_copied'),
      })
    }
  }

  const onClickPreview = async () => {
    const previewUrl = await getPreviewUrl()

    window.open(previewUrl, '_blank')
  }

  const [duplicate] = useMutation(AtsDuplicateJobPostingDocument, {
    extractorFn: (data) => data.atsMutations.duplicateAtsJobPosting,
  })

  const onDuplicate = async () => {
    const result = await duplicate({
      variables: { id: id, authorId: access.id },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success && handled.data.jobPosting) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      navigate(
        navigation.ats.jobs.jobsId.applications.fullPath({ jobId: handled.data.jobPosting.id })
      )
    }
    return handled
  }

  const onPublish = () => {
    if (!companyPageReady) {
      setOpenedModal('company-page-warning')
      return
    }

    track(trackingEvents.ats.publishJobOpeningStart)
    setOpenedModal('publish')
  }

  const onClosePublishedJobPostingModal = () => {
    setOpenedModal(undefined)
    navigate(location.pathname, { replace: true })
  }
  const isOwnerRole = !isReviewerRole && !isEditorRole

  return (
    <>
      <ResourceHeader
        title={title}
        status={{
          label: i18n.t('ats.job_details.job_posting_status_label'),
          text: statusLabel(status),
          variant: statusColor(status),
        }}
        primaryAction={
          isOwnerRole
            ? {
                label:
                  status === 'published'
                    ? i18n.t('ats.job_details.status_options.publish_unlisted')
                    : i18n.t('ats.job_details.status_options.publish'),
                onClick: () => onPublish(),
              }
            : undefined
        }
        secondaryActions={
          isReviewerRole
            ? undefined
            : [
                {
                  label: i18n.t('ats.job_posting_settings.edit_job_button'),
                  icon: Pencil,
                  onClick: () =>
                    navigate(navigation.ats.jobs.jobsId.settings.fullPath({ jobId: id })),
                },
              ]
        }
        otherActions={
          isOwnerRole
            ? [
                status !== 'archived' && {
                  label: i18n.t('ats.job_details.status_options.archive'),
                  onClick: () => setOpenedModal('archive'),
                },
                status !== 'cancelled' && {
                  label: i18n.t('ats.job_details.status_options.cancel'),
                  onClick: () => setOpenedModal('cancel'),
                },
                {
                  label: i18n.t('ats.job_details.copy_url'),
                  onClick: () =>
                    companyPageReady ? copyUrl() : setOpenedModal('company-page-warning'),
                },
                showDuplicateJobButton && {
                  label: i18n.t('ats.job_details.duplicate_job'),
                  onClick: () => onDuplicate(),
                },
                !isJobPostingOpen && {
                  label: i18n.t('common.delete'),
                  onClick: () => setOpenedModal('delete'),
                  critical: true,
                },
              ].filter(Boolean)
            : undefined
        }
        metadata={[
          !!jobLocation?.city && {
            label: i18n.t('ats.job_details.location_label'),
            value: { type: 'text', content: jobLocation.city },
          },
          {
            label: i18n.t('ats.job_details.hiring_team_label'),
            value: {
              type: 'list',
              variant: 'person',
              avatars: hiringManagers.map(
                (hm) =>
                  ({
                    firstName: hm.access?.firstName ?? '',
                    lastName: hm.access?.lastName ?? '',
                    type: 'person',
                    src: buildAvatarUrl(hm.access?.avatar),
                  } satisfies AvatarVariant)
              ),
            },
          },
          !!team?.name && {
            label: i18n.t('ats.job_details.team_label'),
            value: { type: 'text', content: team.name },
          },
          !!numberOfVacancies && {
            label: i18n.t('ats.job_details.number_of_vacancies_label'),
            value: { type: 'text', content: numberOfVacancies.toString() },
          },
          !!startDate && {
            label: i18n.t('ats.job_details.start_date_label'),
            value: { type: 'text', content: startDate.toString() },
          },
          !!requisition?.id && {
            label: i18n.t('ats.job_details.requisition'),
            value: { type: 'text', content: i18n.t('ats.job_details.preview_link') },
            actions: [
              {
                label: i18n.t('ats.job_details.requisition_label'),
                icon: ExternalLink,
                onClick: () =>
                  navigate(
                    navigation.ats.jobs.requisitions.requisitionDetail.fullPath({
                      requisition_id: requisition.id,
                    })
                  ),
              },
            ],
          },
          {
            label: i18n.t('ats.job_details.preview_job'),
            value: { type: 'text', content: i18n.t('ats.job_details.preview_link') },
            actions: [
              {
                label: i18n.t('ats.job_details.preview_label'),
                icon: ExternalLink,
                onClick: () =>
                  companyPageReady ? onClickPreview() : setOpenedModal('company-page-warning'),
              },
            ],
          },
        ]}
      />

      {openedModal === 'archive' && (
        <ArchiveJobPostingModal
          careersUrl={careersUrl}
          id={id}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
      {openedModal === 'cancel' && (
        <CancelJobPostingModal
          careersUrl={careersUrl}
          id={id}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
      {openedModal === 'publish' && (
        <PublishJobPostingModal
          job={jobData}
          onSuccess={(value) => {
            setOpenedModal(undefined)
            value === 'published' && setOpenedModal('published')
          }}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
      {openedModal === 'published' && (
        <PublishedJobPostingModal id={id} onClose={() => onClosePublishedJobPostingModal()} />
      )}
      {openedModal === 'company-page-warning' && (
        <CompanyPageNotConfiguredModal onClose={() => setOpenedModal(undefined)} />
      )}

      {openedModal === 'delete' && (
        <DeleteJobPostingModal
          id={id}
          onClose={() => {
            navigate(navigation.ats.jobs.fullPath())
          }}
        />
      )}
    </>
  )
}
